import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { DateRange } from "react-day-picker";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  CheckCircle,
  Eye,
  TrendingUp,
  TrendingDown,
  UserCheck,
  UserX,
  AlertCircle,
  AlertTriangle,
  GraduationCap,
  BarChart3,
  Download,
  Filter,
  Bell,
  Shield,
  Zap,
  Search,
  Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import Alert from "@/components/ui/alert";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";

// Import advanced components
import AdvancedAnalyticsDashboard from "@/components/analytics/AdvancedAnalyticsDashboard";
import NotificationManagement from "@/components/notifications/NotificationManagement";

interface Meeting {
  id: number;
  title: string;
  meetingDate: string;
  startTime: string;
  endTime: string;
  location: string;
  agenda: string;
  minutes: string;
  isConducted: boolean;
  totalAttendees: number;
  attendances: MeetingAttendance[];
  createdAt: string;
  updatedAt: string;
}

interface MeetingAttendance {
  id: number;
  isPresent: boolean;
  notes: string;
  teacher: {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Teacher {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  teacherId: string;
  isGroupLeader: boolean;
  leadershipRole: string;
}

interface AnalyticsData {
  overview: {
    totalMeetings: number;
    completedMeetings: number;
    totalTeachers: number;
    averageAttendance: number;
    completionRate: number;
  };
  trends: {
    current: number;
    previous: number;
    direction: 'improving' | 'declining' | 'stable';
    change: number;
  };
  insights: Array<{
    type: 'positive' | 'neutral' | 'warning';
    message: string;
    recommendation: string;
  }>;
}

export default function EnterpriseAttendance() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isViewMeetingSheetOpen, setIsViewMeetingSheetOpen] = useState(false);
  const [isRecordAttendanceDialogOpen, setIsRecordAttendanceDialogOpen] = useState(false);
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<{ teacherId: number; isPresent: boolean; notes: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfMonth(subDays(new Date(), 30)),
    to: endOfMonth(new Date())
  });
  
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      setDateRange({ from: range.from, to: range.to });
    }
  };

  // Fetch meetings
  const { data: meetingsData, isLoading: isLoadingMeetings } = useQuery<
    { success: boolean; data: Meeting[]; count: number }
  >("meetings", async () => {
    const response = await api.get("/api/meetings");
    return response.data;
  });

  // Fetch teachers
  const { data: teachersData, isLoading: isLoadingTeachers } = useQuery<
    { success: boolean; data: Teacher[]; count: number }
  >("teachers", async () => {
    const response = await api.get("/api/teachers");
    return response.data;
  });

  // Fetch analytics
  const { data: analyticsData } = useQuery<AnalyticsData>(
    ["analytics", dateRange],
    async () => {
      const response = await api.get("/api/attendance/analytics", {
        params: {
          startDate: format(dateRange.from, "yyyy-MM-dd"),
          endDate: format(dateRange.to, "yyyy-MM-dd")
        }
      });
      return response.data.data;
    }
  );

  const meetings = meetingsData?.data || [];
  const teachers = teachersData?.data || [];
  const analytics = analyticsData || {
    overview: { totalMeetings: 0, completedMeetings: 0, totalTeachers: 0, averageAttendance: 0, completionRate: 0 },
    trends: { current: 0, previous: 0, direction: 'stable', change: 0 },
    insights: []
  };

  // Filter meetings based on search and filters
  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         meeting.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" ||
                         (filterStatus === "completed" && meeting.isConducted) ||
                         (filterStatus === "upcoming" && !meeting.isConducted) ||
                         (filterStatus === "overdue" && !meeting.isConducted && new Date(meeting.meetingDate) < new Date());
    
    return matchesSearch && matchesStatus;
  });

  // Mutation for recording attendance
  const recordAttendanceMutation = useMutation(
    async ({ meetingId, teacherId, isPresent, notes }: { meetingId: number; teacherId: number; isPresent: boolean; notes: string }) => {
      const response = await api.post(`/api/meetings/${meetingId}/attendance`, {
        teacherId,
        isPresent,
        notes,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Attendance recorded successfully!");
        queryClient.invalidateQueries("meetings");
        queryClient.invalidateQueries("teachers");
        setIsRecordAttendanceDialogOpen(false);
      },
      onError: (error: any) => {
        toast.error("Failed to record attendance", {
          description: error.response?.data?.message || "An unknown error occurred.",
        });
      },
    }
  );

  // Bulk update mutation
  const bulkUpdateMutation = useMutation(
    async ({ meetingId, attendanceUpdates }: { meetingId: number; attendanceUpdates: any[] }) => {
      const response = await api.post("/api/attendance/bulk-update", {
        meetingId,
        attendanceUpdates,
        reason: "Bulk attendance update"
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Bulk attendance update completed!");
        queryClient.invalidateQueries("meetings");
        setIsBulkUpdateDialogOpen(false);
      },
      onError: (error: any) => {
        toast.error("Failed to update attendance", {
          description: error.response?.data?.message || "An unknown error occurred.",
        });
      },
    }
  );

  const handleViewMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setIsViewMeetingSheetOpen(true);
  };

  const handleRecordAttendanceClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    const initialAttendance = teachers.map(teacher => {
      const existingAttendance = meeting.attendances.find(att => att.teacher.id === teacher.id);
      return {
        teacherId: teacher.id,
        isPresent: existingAttendance ? existingAttendance.isPresent : false,
        notes: existingAttendance ? existingAttendance.notes : "",
      };
    });
    setAttendanceRecords(initialAttendance);
    setIsRecordAttendanceDialogOpen(true);
  };

  const handleBulkUpdateClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    const initialAttendance = teachers.map(teacher => {
      const existingAttendance = meeting.attendances.find(att => att.teacher.id === teacher.id);
      return {
        teacherId: teacher.id,
        isPresent: existingAttendance ? existingAttendance.isPresent : false,
        notes: existingAttendance ? existingAttendance.notes : "",
      };
    });
    setAttendanceRecords(initialAttendance);
    setIsBulkUpdateDialogOpen(true);
  };

  const handleAttendanceChange = (teacherId: number, isPresent: boolean) => {
    setAttendanceRecords(prev =>
      prev.map(record =>
        record.teacherId === teacherId ? { ...record, isPresent } : record
      )
    );
  };

  const handleAttendanceNotesChange = (teacherId: number, notes: string) => {
    setAttendanceRecords(prev =>
      prev.map(record =>
        record.teacherId === teacherId ? { ...record, notes } : record
      )
    );
  };

  const handleSubmitAttendance = async () => {
    if (!selectedMeeting) return;

    try {
      for (const record of attendanceRecords) {
        await recordAttendanceMutation.mutateAsync({
          meetingId: selectedMeeting.id,
          teacherId: record.teacherId,
          isPresent: record.isPresent,
          notes: record.notes,
        });
      }
      toast.success("All attendance records submitted successfully!");
      setIsRecordAttendanceDialogOpen(false);
      queryClient.invalidateQueries("meetings");
    } catch (error) {
      toast.error("Failed to submit some attendance records.");
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedMeeting) return;

    const attendanceUpdates = attendanceRecords.map(record => ({
      teacherId: record.teacherId,
      isPresent: record.isPresent,
      notes: record.notes
    }));

    bulkUpdateMutation.mutate({
      meetingId: selectedMeeting.id,
      attendanceUpdates
    });
  };

  const handleExportReport = async (reportFormat: 'csv' | 'pdf') => {
    try {
      const response = await api.post("/api/attendance/reports/generate", {
        startDate: format(dateRange.from, "yyyy-MM-dd"),
        endDate: format(dateRange.to, "yyyy-MM-dd"),
        reportType: 'detailed',
        format: reportFormat,
        includeDetails: true
      });

      if (reportFormat === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error("Failed to export report");
    }
  };

  const getMeetingStatus = (meeting: Meeting) => {
    const meetingDateTime = new Date(`${meeting.meetingDate}T${meeting.endTime}`);
    const now = new Date();

    if (meeting.isConducted) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
    } else if (meetingDateTime < now) {
      return <Badge variant="destructive">Overdue</Badge>;
    } else if (format(meetingDateTime, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Today</Badge>;
    } else {
      return <Badge variant="outline">Upcoming</Badge>;
    }
  };

  const getAttendanceRate = (meeting: Meeting) => {
    if (!meeting.attendances || meeting.attendances.length === 0) return "0%";
    const presentCount = meeting.attendances.filter(att => att.isPresent).length;
    const totalCount = meeting.attendances.length;
    return `${((presentCount / totalCount) * 100).toFixed(0)}%`;
  };

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="p-6 space-y-6 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#050500' }}>
              Enterprise Attendance Management
            </h1>
            <p className="text-lg" style={{ color: '#050500', opacity: 0.7 }}>
              Advanced attendance tracking with analytics and automation
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAnalyticsOpen(true)}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsNotificationsOpen(true)}
            >
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportReport('csv')}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportReport('pdf')}
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.totalMeetings}</div>
              <p className="text-xs text-muted-foreground">
                {analytics.overview.completedMeetings} completed
              </p>
              <Progress value={analytics.overview.completionRate} className="mt-2 h-2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg. Attendance</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.averageAttendance}%</div>
              <div className="flex items-center text-xs text-muted-foreground">
                {analytics.trends.direction === 'improving' && <TrendingUp className="w-3 h-3 mr-1 text-green-600" />}
                {analytics.trends.direction === 'declining' && <TrendingDown className="w-3 h-3 mr-1 text-red-600" />}
                {analytics.trends.direction === 'stable' && <AlertCircle className="w-3 h-3 mr-1 text-gray-600" />}
                {analytics.trends.change > 0 ? '+' : ''}{analytics.trends.change}% vs previous
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.completionRate}%</div>
              <p className="text-xs text-muted-foreground">
                Meetings marked as conducted
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Members</CardTitle>
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.totalTeachers}</div>
              <p className="text-xs text-muted-foreground">
                Registered in the system
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        {analytics.insights.length > 0 && (
          <div className="space-y-3">
            {analytics.insights.map((insight) => (
              <Alert>
                <div className="flex items-start gap-2">
                  {insight.type === 'positive' && <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />}
                  {insight.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />}
                  {insight.type === 'neutral' && <BarChart3 className="w-4 h-4 text-blue-600 mt-0.5" />}
                  <div>
                    <div className="font-medium">
                      {insight.message}
                    </div>
                    <div className="text-sm mt-1">
                      <strong>Recommendation:</strong> {insight.recommendation}
                    </div>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        )}

        {/* Filters and Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Advanced Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-1">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search meetings by title or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Status:</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label>Date Range:</Label>
                <DatePickerWithRange
                  date={dateRange}
                  onDateChange={handleDateRangeChange}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="attendance-management">Attendance Management</TabsTrigger>
            <TabsTrigger value="meeting-attendance">Meeting Attendance</TabsTrigger>
            <TabsTrigger value="teacher-attendance">Member Performance</TabsTrigger>
            <TabsTrigger value="bulk-operations">Bulk Operations</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.overview.totalMeetings}</div>
                  <p className="text-xs text-muted-foreground">
                    {analytics.overview.completedMeetings} completed
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg. Attendance</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.overview.averageAttendance}%</div>
                  <p className="text-xs text-muted-foreground">
                    Across all meetings
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.overview.completionRate}%</div>
                  <p className="text-xs text-muted-foreground">
                    Meetings marked as conducted
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                  <GraduationCap className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.overview.totalTeachers}</div>
                  <p className="text-xs text-muted-foreground">
                    Registered in the system
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Attendance Trends</CardTitle>
                <CardDescription>Overview of attendance performance over time.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-48 flex items-center justify-center text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mr-2" /> Advanced analytics available in Analytics tab
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Management Tab */}
          <TabsContent value="attendance-management" className="mt-6">
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Quick Attendance Actions
                  </CardTitle>
                  <CardDescription>
                    Manage attendance for upcoming meetings and view attendance status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="w-8 h-8 text-blue-600" />
                        <div>
                          <h3 className="font-semibold">Upcoming Meetings</h3>
                          <p className="text-sm text-muted-foreground">
                            {meetings.filter(m => !m.isConducted && new Date(m.meetingDate) >= new Date()).length} meetings
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={() => setActiveTab("meeting-attendance")}
                        style={{ backgroundColor: '#e1f812', color: '#050500' }}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        View Upcoming
                      </Button>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <Users className="w-8 h-8 text-green-600" />
                        <div>
                          <h3 className="font-semibold">Total Members</h3>
                          <p className="text-sm text-muted-foreground">
                            {teachers.length} registered members
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => setActiveTab("teacher-attendance")}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        View Performance
                      </Button>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <Zap className="w-8 h-8 text-purple-600" />
                        <div>
                          <h3 className="font-semibold">Bulk Operations</h3>
                          <p className="text-sm text-muted-foreground">
                            Update multiple records
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => setActiveTab("bulk-operations")}
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        Bulk Update
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Attendance Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Recent Attendance Activity
                  </CardTitle>
                  <CardDescription>
                    Latest attendance records and updates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {meetings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p>No meetings scheduled yet</p>
                      <p className="text-sm">Schedule meetings to start tracking attendance</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {meetings
                        .filter(m => m.attendances && m.attendances.length > 0)
                        .slice(0, 5)
                        .map((meeting) => (
                          <div key={meeting.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-4 h-4 text-blue-600" />
                              <div>
                                <div className="font-medium">{meeting.title}</div>
                                <div className="text-sm text-muted-foreground">
                                  {format(new Date(meeting.meetingDate), "MMM d, yyyy")} • {meeting.location}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                meeting.attendances.filter(a => a.isPresent).length >= meeting.attendances.length * 0.8 
                                  ? "default" 
                                  : meeting.attendances.filter(a => a.isPresent).length >= meeting.attendances.length * 0.5 
                                  ? "secondary" 
                                  : "destructive"
                              }>
                                {meeting.attendances.filter(a => a.isPresent).length}/{meeting.attendances.length} present
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewMeeting(meeting)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      
                      {meetings.filter(m => m.attendances && m.attendances.length > 0).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                          <p>No attendance records yet</p>
                          <p className="text-sm">Start recording attendance for meetings</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Attendance Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Attendance Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">This Month</span>
                        <span className="text-lg font-bold text-blue-600">
                          {analytics.overview.averageAttendance}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Trend</span>
                        <div className="flex items-center gap-1">
                          {analytics.trends.direction === 'improving' && <TrendingUp className="w-4 h-4 text-green-600" />}
                          {analytics.trends.direction === 'declining' && <TrendingDown className="w-4 h-4 text-red-600" />}
                          {analytics.trends.direction === 'stable' && <AlertCircle className="w-4 h-4 text-gray-600" />}
                          <span className="text-sm">
                            {analytics.trends.change > 0 ? '+' : ''}{analytics.trends.change}%
                          </span>
                        </div>
                      </div>
                      <Progress value={analytics.overview.averageAttendance} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Compliance Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Compliant Members</span>
                        <span className="text-lg font-bold text-green-600">
                          {teachers.filter(t => {
                            const teacherMeetings = meetings.filter(m => 
                              m.attendances.some(a => a.teacher.id === t.id)
                            );
                            const attendedMeetings = teacherMeetings.filter(m => 
                              m.attendances.some(a => a.teacher.id === t.id && a.isPresent)
                            );
                            const attendanceRate = teacherMeetings.length > 0 
                              ? (attendedMeetings.length / teacherMeetings.length) * 100 
                              : 0;
                            return attendanceRate >= 80;
                          }).length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Needs Attention</span>
                        <span className="text-lg font-bold text-red-600">
                          {teachers.filter(t => {
                            const teacherMeetings = meetings.filter(m => 
                              m.attendances.some(a => a.teacher.id === t.id)
                            );
                            const attendedMeetings = teacherMeetings.filter(m => 
                              m.attendances.some(a => a.teacher.id === t.id && a.isPresent)
                            );
                            const attendanceRate = teacherMeetings.length > 0 
                              ? (attendedMeetings.length / teacherMeetings.length) * 100 
                              : 0;
                            return attendanceRate < 70;
                          }).length}
                        </span>
                      </div>
                      <Progress 
                        value={teachers.length > 0 ? (teachers.filter(t => {
                          const teacherMeetings = meetings.filter(m => 
                            m.attendances.some(a => a.teacher.id === t.id)
                          );
                          const attendedMeetings = teacherMeetings.filter(m => 
                            m.attendances.some(a => a.teacher.id === t.id && a.isPresent)
                          );
                          const attendanceRate = teacherMeetings.length > 0 
                            ? (attendedMeetings.length / teacherMeetings.length) * 100 
                            : 0;
                          return attendanceRate >= 80;
                        }).length / teachers.length) * 100 : 0} 
                        className="h-2" 
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Meeting Attendance Tab */}
          <TabsContent value="meeting-attendance" className="mt-6">
            <div className="space-y-6">
              {/* Attendance Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Attendance Actions
                  </CardTitle>
                  <CardDescription>
                    Manage attendance for meetings that need attention
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg bg-yellow-50">
                      <div className="flex items-center gap-3 mb-3">
                        <AlertTriangle className="w-8 h-8 text-yellow-600" />
                        <div>
                          <h3 className="font-semibold">Pending Attendance</h3>
                          <p className="text-sm text-muted-foreground">
                            {meetings.filter(m => !m.isConducted && new Date(m.meetingDate) <= new Date()).length} meetings need attendance
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => {
                          const overdueMeetings = meetings.filter(m => !m.isConducted && new Date(m.meetingDate) <= new Date());
                          if (overdueMeetings.length > 0) {
                            handleRecordAttendanceClick(overdueMeetings[0]);
                          }
                        }}
                        disabled={meetings.filter(m => !m.isConducted && new Date(m.meetingDate) <= new Date()).length === 0}
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Record Overdue Attendance
                      </Button>
                    </div>

                    <div className="p-4 border rounded-lg bg-blue-50">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="w-8 h-8 text-blue-600" />
                        <div>
                          <h3 className="font-semibold">Upcoming Meetings</h3>
                          <p className="text-sm text-muted-foreground">
                            {meetings.filter(m => !m.isConducted && new Date(m.meetingDate) > new Date()).length} meetings scheduled
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => {
                          const upcomingMeetings = meetings.filter(m => !m.isConducted && new Date(m.meetingDate) > new Date());
                          if (upcomingMeetings.length > 0) {
                            handleRecordAttendanceClick(upcomingMeetings[0]);
                          }
                        }}
                        disabled={meetings.filter(m => !m.isConducted && new Date(m.meetingDate) > new Date()).length === 0}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Record Upcoming Attendance
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Meeting List */}
              <Card>
              <CardHeader>
                <CardTitle>Meeting Attendance Overview</CardTitle>
                <CardDescription>Track attendance for each scheduled meeting.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingMeetings ? (
                  <div className="text-center py-8">Loading meetings...</div>
                ) : filteredMeetings.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No meetings found matching your criteria.</div>
                ) : (
                  <div className="space-y-4">
                    {filteredMeetings.map((meeting) => (
                      <div key={meeting.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{meeting.title}</h3>
                              {getMeetingStatus(meeting)}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {format(new Date(meeting.meetingDate), "PPP")} at {meeting.startTime}
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {meeting.location}
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {meeting.totalAttendees}/{teachers.length} attended
                                <Progress 
                                  value={parseFloat(getAttendanceRate(meeting))} 
                                  className="w-16 h-2"
                                />
                              </div>
                            </div>
                            
                            {meeting.agenda && (
                              <div className="mt-2">
                                <p className="text-sm text-gray-600">
                                  <strong>Agenda:</strong> {meeting.agenda}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewMeeting(meeting)}
                            >
                              <Eye className="h-4 w-4 mr-2" /> View Details
                            </Button>
                            
                            {!meeting.isConducted && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRecordAttendanceClick(meeting)}
                                >
                                  <UserCheck className="h-4 w-4 mr-2" /> Record Attendance
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleBulkUpdateClick(meeting)}
                                >
                                  <Zap className="h-4 w-4 mr-2" /> Bulk Update
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Member Attendance Tab */}
          <TabsContent value="teacher-attendance" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Individual Member Attendance</CardTitle>
                <CardDescription>View attendance records for each member.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingTeachers ? (
                  <div className="text-center py-8">Loading members...</div>
                ) : teachers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No members registered yet.</div>
                ) : (
                  <div className="space-y-4">
                    {teachers.map((teacher) => {
                      const teacherMeetings = meetings.filter(m =>
                        m.attendances.some(att => att.teacher.id === teacher.id)
                      );
                      const attendedCount = teacherMeetings.filter(m =>
                        m.attendances.some(att => att.teacher.id === teacher.id && att.isPresent)
                      ).length;
                      const totalTeacherMeetings = teacherMeetings.length;
                      const teacherAttendanceRate = totalTeacherMeetings > 0
                        ? ((attendedCount / totalTeacherMeetings) * 100).toFixed(0)
                        : "0";

                      return (
                        <div key={teacher.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg">{teacher.fullName}</h3>
                                <Badge variant="outline">{teacher.teacherId}</Badge>
                                {teacher.isGroupLeader && (
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                    {teacher.leadershipRole}
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                  <span className="text-sm">Attendance Rate: {teacherAttendanceRate}%</span>
                                </div>
                                <Progress 
                                  value={parseFloat(teacherAttendanceRate)} 
                                  className="w-32 h-2"
                                />
                              </div>
                            </div>
                            
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 h-4 mr-2" /> View History
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Operations Tab */}
          <TabsContent value="bulk-operations" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Bulk Operations</CardTitle>
                <CardDescription>Perform bulk operations on attendance records.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <div>
                      Bulk operations allow you to update multiple attendance records at once. 
                      Use with caution as these changes cannot be easily undone.
                    </div>
                  </Alert>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Bulk Attendance Update</CardTitle>
                        <CardDescription>Update attendance for multiple members at once</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          className="w-full" 
                          onClick={() => {
                            if (meetings.length > 0) {
                              handleBulkUpdateClick(meetings[0]);
                            }
                          }}
                          disabled={meetings.length === 0}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Start Bulk Update
                        </Button>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Export Operations</CardTitle>
                        <CardDescription>Export attendance data in various formats</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={() => handleExportReport('csv')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV
                          </Button>
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={() => handleExportReport('pdf')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export PDF
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Meeting Details Sheet */}
        <Sheet open={isViewMeetingSheetOpen} onOpenChange={setIsViewMeetingSheetOpen}>
          <SheetContent className="w-[400px] sm:w-[540px]">
            <SheetHeader>
              <SheetTitle>{selectedMeeting?.title}</SheetTitle>
              <SheetDescription>Details and attendance for this meeting.</SheetDescription>
            </SheetHeader>
            {selectedMeeting && (
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label className="text-right">Date</Label>
                  <span className="col-span-2">
                    {format(new Date(selectedMeeting.meetingDate), "PPP")}
                  </span>
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label className="text-right">Time</Label>
                  <span className="col-span-2">
                    {selectedMeeting.startTime} - {selectedMeeting.endTime}
                  </span>
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label className="text-right">Location</Label>
                  <span className="col-span-2">{selectedMeeting.location}</span>
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label className="text-right">Status</Label>
                  <span className="col-span-2">{getMeetingStatus(selectedMeeting)}</span>
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label className="text-right">Agenda</Label>
                  <span className="col-span-2">{selectedMeeting.agenda || "N/A"}</span>
                </div>
                <div className="grid grid-cols-3 items-start gap-4">
                  <Label htmlFor="minutes" className="text-right pt-2">Minutes</Label>
                  <Textarea
                    id="minutes"
                    value={selectedMeeting.minutes || ""}
                    className="col-span-2"
                    disabled={selectedMeeting.isConducted}
                  />
                </div>

                <h3 className="text-lg font-semibold mt-4">Attendance Records</h3>
                {selectedMeeting.attendances.length === 0 ? (
                  <p className="text-muted-foreground">No attendance recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedMeeting.attendances.map((attendance) => (
                      <div key={attendance.id} className="flex items-center justify-between p-2 border rounded">
                        <span>{attendance.teacher.fullName}</span>
                        <div className="flex items-center gap-2">
                          {attendance.isPresent ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Present
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <UserX className="w-3 h-3 mr-1" />
                              Absent
                            </Badge>
                          )}
                          {attendance.notes && (
                            <span className="text-xs text-muted-foreground">({attendance.notes})</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Record Attendance Dialog */}
        <Dialog open={isRecordAttendanceDialogOpen} onOpenChange={setIsRecordAttendanceDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Record Attendance for {selectedMeeting?.title}</DialogTitle>
              <DialogDescription>
                Mark members as present or absent for this meeting.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {isLoadingTeachers ? (
                <div className="text-center">Loading members...</div>
              ) : (
                <ScrollArea className="h-96">
                  <div className="space-y-2">
                    {teachers.map((teacher) => {
                      const record = attendanceRecords.find(r => r.teacherId === teacher.id);
                      return (
                        <div key={teacher.id} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={record?.isPresent || false}
                              onCheckedChange={(checked) =>
                                handleAttendanceChange(teacher.id, checked as boolean)
                              }
                            />
                            <div>
                              <div className="font-medium">{teacher.fullName}</div>
                              <div className="text-sm text-muted-foreground">{teacher.teacherId}</div>
                            </div>
                          </div>
                          <Input
                            value={record?.notes || ""}
                            onChange={(e) => handleAttendanceNotesChange(teacher.id, e.target.value)}
                            placeholder="Optional notes"
                            className="w-48"
                          />
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
            <DialogFooter>
              <Button
                type="submit"
                onClick={handleSubmitAttendance}
                disabled={recordAttendanceMutation.isLoading}
              >
                {recordAttendanceMutation.isLoading ? "Saving..." : "Save Attendance"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Update Dialog */}
        <Dialog open={isBulkUpdateDialogOpen} onOpenChange={setIsBulkUpdateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Bulk Update Attendance for {selectedMeeting?.title}</DialogTitle>
              <DialogDescription>
                Update attendance for multiple members at once. This operation cannot be easily undone.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <div>
                  This is a bulk operation that will update all attendance records at once. 
                  Please review your changes carefully before proceeding.
                </div>
              </Alert>
              
              {isLoadingTeachers ? (
                <div className="text-center">Loading members...</div>
              ) : (
                <ScrollArea className="h-96">
                  <div className="space-y-2">
                    {teachers.map((teacher) => {
                      const record = attendanceRecords.find(r => r.teacherId === teacher.id);
                      return (
                        <div key={teacher.id} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={record?.isPresent || false}
                              onCheckedChange={(checked) =>
                                handleAttendanceChange(teacher.id, checked as boolean)
                              }
                            />
                            <div>
                              <div className="font-medium">{teacher.fullName}</div>
                              <div className="text-sm text-muted-foreground">{teacher.teacherId}</div>
                            </div>
                          </div>
                          <Input
                            value={record?.notes || ""}
                            onChange={(e) => handleAttendanceNotesChange(teacher.id, e.target.value)}
                            placeholder="Optional notes"
                            className="w-48"
                          />
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
            <DialogFooter>
              <Button
                type="submit"
                onClick={handleBulkUpdate}
                disabled={bulkUpdateMutation.isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {bulkUpdateMutation.isLoading ? "Updating..." : "Confirm Bulk Update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Analytics Modal */}
        <Dialog open={isAnalyticsOpen} onOpenChange={setIsAnalyticsOpen}>
          <DialogContent className="max-w-7xl">
            <DialogHeader>
              <DialogTitle>Advanced Analytics Dashboard</DialogTitle>
              <DialogDescription>
                Comprehensive attendance analytics and insights
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto px-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
              <AdvancedAnalyticsDashboard />
            </div>
          </DialogContent>
        </Dialog>

        {/* Notifications Modal */}
        <Dialog open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
          <DialogContent className="max-w-7xl">
            <DialogHeader>
              <DialogTitle>Notification Management</DialogTitle>
              <DialogDescription>
                Configure and manage automated notifications
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto px-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
              <NotificationManagement />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
